import mark from './perfectSliceMark.json';
import { formatPercent } from '../game/rules.mjs';
import { multiPath } from '../game/geometry.mjs';

// Format the error value for the daily share text:
// rounds to at most 2 d.p. and strips trailing zeros.
// A non-zero difference that rounds to "0.00" (< 0.005% off) is rendered as "<0.01".
function formatShareError(diff) {
  const rounded = parseFloat(diff.toFixed(2));
  if (rounded === 0) return '<0.01';
  return rounded.toString();
}

export function resultShareText(data) {
  const daily = data.mode === 'daily';

  if (!daily) {
    // Endless mode: keep the original format completely unchanged.
    return `Perfect Slice\nby CubitPlay\n\nEndless · Round ${data.round}\nTarget: ${data.target}%\nMy cut: ${formatPercent(data.best.percent)}%\n${data.best.points} pts · ${formatPercent(data.best.difference)}% off\nBest of ${data.attempts} ${data.attempts === 1 ? 'cut' : 'cuts'}\n\nThink you can beat that?\n${data.url}`;
  }

  // Daily mode: short, celebratory three-line format.
  const diff = data.best.difference;
  let line1;
  if (diff === 0) {
    line1 = 'PERFECT SLICE. 🎯🔥';
  } else if (diff <= 0.10) {
    line1 = `Just ${formatShareError(diff)}% away from a Perfect Slice. 🎯`;
  } else if (diff <= 0.50) {
    line1 = `So close — just ${formatShareError(diff)}% off. 👀`;
  } else if (diff <= 1.00) {
    line1 = 'Less than 1% off the target. Not bad. 😏';
  } else {
    line1 = 'Thought I could eyeball it. Apparently not. 😅';
  }

  return `${line1}\nThink you can beat that?\n${data.url}`;
}

function drawMark(context, x, y, size) {
  context.save(); context.translate(x, y); context.scale(size / 64, size / 64);
  context.fillStyle = mark.orange; context.fill(new Path2D(mark.top));
  context.fillStyle = mark.deep; context.fill(new Path2D(mark.bottom)); context.restore();
}
function drawCut(context, best, x, y, width, height) {
  const points = [...(best.selected || []), ...(best.remainder || [])].flat(2);
  if (!points.length) { drawMark(context, x, y, Math.min(width, height)); return; }
  const xs = points.map(p => p[0]), ys = points.map(p => p[1]);
  const left = Math.min(...xs), top = Math.min(...ys), w = Math.max(...xs) - left, h = Math.max(...ys) - top;
  const scale = Math.min((width - 36) / w, (height - 36) / h);
  const [nx, ny] = best.normal || [0, -1];
  context.save(); context.translate(x + (width - w * scale) / 2, y + (height - h * scale) / 2); context.scale(scale, scale); context.translate(-left, -top);
  context.save(); context.translate(nx * 7, ny * 7); context.fillStyle = mark.orange; context.fill(new Path2D(multiPath(best.selected)), 'evenodd'); context.restore();
  context.save(); context.translate(-nx * 7, -ny * 7); context.fillStyle = mark.deep; context.fill(new Path2D(multiPath(best.remainder)), 'evenodd'); context.restore();
  context.restore();
}

function drawHeader(context, text, line, wide, data) {
  const daily = data.mode === 'daily';
  drawMark(context, 62, 49, wide ? 66 : 78);
  const wordX = wide ? 145 : 158;
  text('Perfect', wordX, wide ? 80 : 87, wide ? 34 : 42, mark.charcoal, 800);
  const width = context.measureText('Perfect').width;
  context.fillStyle = mark.orange; context.save(); context.translate(wordX + width + 13, wide ? 56 : 58); context.transform(1, 0, -.45, 1, 0, 0); context.fillRect(0, 0, 3, wide ? 23 : 29); context.restore();
  text('Slice', wordX + width + 28, wide ? 80 : 87, wide ? 34 : 42, mark.charcoal, 800);
  text('by CubitPlay', wordX + 1, wide ? 108 : 121, wide ? 17 : 21, mark.muted);
  line(wide ? 140 : 168);
  const modeLabel = daily ? `DAILY SLICE #${data.number}` : `ENDLESS · ROUND ${data.round}`;
  text(modeLabel, 68, wide ? 186 : 224, wide ? 22 : 26, mark.charcoal, 700);
  const note = daily ? `${data.date} UTC${data.scope === 'preview' ? ' · PREVIEW' : ''}` : `BEST OF ${data.attempts} ${data.attempts === 1 ? 'CUT' : 'CUTS'}`;
  text(note, 68, wide ? 219 : 264, wide ? 18 : 22, mark.muted);
}

function drawScore(context, text, wide, data) {
  text('OFF THE TARGET', 70, wide ? 276 : 361, wide ? 17 : 22, mark.muted, 600);
  const accuracy = `${formatPercent(data.best.difference)}%`;
  text(accuracy, 61, wide ? 373 : 491, wide ? 108 : 136, mark.charcoal, 800);
  text(`${data.best.points}`, 70, wide ? 428 : 568, wide ? 43 : 58, mark.charcoal, 700);
  const scoreWidth = context.measureText(String(data.best.points)).width;
  text('pts', 80 + scoreWidth, wide ? 428 : 568, wide ? 23 : 30, mark.muted);
  drawCut(context, data.best, wide ? 713 : 631, wide ? 198 : 312, wide ? 383 : 354, wide ? 308 : 306);
}

export async function renderShareCard(data, format = 'square') {
  if (!data?.best || ![data.target, data.best.percent, data.best.points, data.best.difference].every(Number.isFinite)) throw new Error('No saved slice to share yet.');
  // Canvas pixels have no remote images; export remains safe/offline after load.
  if (document.fonts?.ready) await Promise.race([document.fonts.ready, new Promise(resolve => setTimeout(resolve, 1600))]);
  const wide = format === 'landscape';
  const canvas = document.createElement('canvas'); canvas.width = wide ? 1200 : 1080; canvas.height = wide ? 630 : 1080;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('This browser couldn’t create the image. You can still copy your result.');
  context.fillStyle = mark.paper; context.fillRect(0, 0, canvas.width, canvas.height);
  const text = (value, x, y, size = 24, color = mark.charcoal, weight = 500) => {
    context.fillStyle = color; context.font = `${weight} ${size}px "Figtree", sans-serif`; context.fillText(String(value), x, y);
  };
  const line = y => { context.strokeStyle = mark.border; context.lineWidth = 2; context.beginPath(); context.moveTo(64, y); context.lineTo(canvas.width - 64, y); context.stroke(); };
  const daily = data.mode === 'daily';
  drawHeader(context, text, line, wide, data);
  drawScore(context, text, wide, data);
  const labelY = wide ? 477 : 686, valueY = wide ? 513 : 745;
  if (!wide) line(622);
  text('TARGET', 70, labelY, wide ? 16 : 22, mark.muted, 600);
  text('YOUR CUT', wide ? 293 : 365, labelY, wide ? 16 : 22, mark.muted, 600);
  text(`${data.target}%`, 68, valueY, wide ? 30 : 48, mark.charcoal, 600);
  text(`${formatPercent(data.best.percent)}%`, wide ? 291 : 363, valueY, wide ? 30 : 48, mark.charcoal, 600);
  if (!wide) {
    text('Think you can beat that?', 68, 861, 39, mark.charcoal, 700);
    text(daily ? 'Three cuts. Your best counts.' : 'One straight cut. Trust your eyes.', 70, 912, 26, mark.muted);
  }
  line(wide ? 548 : 967);
  const url = new URL(data.url);
  const displayUrl = `${url.host}${url.pathname}`;
  const footerSize = wide ? 19 : 22;
  context.font = `500 ${footerSize}px "Figtree", sans-serif`;
  const available = canvas.width - 136;
  text(displayUrl, 70, wide ? 594 : 1020, context.measureText(displayUrl).width > available ? 16 : footerSize, mark.muted);
  const blob = await new Promise((resolve, reject) => canvas.toBlob(result => result ? resolve(result) : reject(new Error('Image export failed. Try again, or copy your result.')), 'image/png'));
  return { blob, text: resultShareText(data), filename: `perfect-slice-${daily ? `daily-${data.date}` : `round-${data.round}`}-${format}.png` };
}
